import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { authClient } from '../lib/authClient';

function ForgotPasswordPage() {
  const { isDarkMode, toggleTheme, themeClasses } = useTheme();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  useEffect(() => {
    const handleStorageChange = () => {
      const newLanguage = localStorage.getItem('language') || 'english';
      setLanguage(newLanguage);
      document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
      document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  const toggleLanguage = () => {
    const newLanguage = language === 'english' ? 'hebrew' : 'english';
    localStorage.setItem('language', newLanguage);
    setLanguage(newLanguage);
    document.documentElement.dir = newLanguage === 'hebrew' ? 'rtl' : 'ltr';
    document.documentElement.lang = newLanguage === 'hebrew' ? 'he' : 'en';
    window.dispatchEvent(new Event('languageChange'));
  };

  const translations = {
    english: {
      'forgot.title': 'Forgot password?',
      'forgot.subtitle': "Enter your email and we'll send you a link to reset your password.",
      'forgot.email': 'Email Address',
      'forgot.submit': 'Send reset link',
      'forgot.sending': 'Sending…',
      'forgot.success': 'Check your email for the reset link. If you don’t see it, check your spam folder.',
      'forgot.backToLogin': 'Back to sign in',
      'forgot.error': 'Something went wrong. Please try again.',
      'brand.name': 'BetterChoice',
      'brand.portal': 'Dietitian Portal',
      'footer.copyright': '© 2026 BetterChoice. All rights reserved.',
    },
    hebrew: {
      'forgot.title': 'שכחת סיסמה?',
      'forgot.subtitle': 'הזן את האימייל שלך ונשלח לך קישור לאיפוס הסיסמה.',
      'forgot.email': 'כתובת אימייל',
      'forgot.submit': 'שלח קישור לאיפוס',
      'forgot.sending': 'שולח…',
      'forgot.success': 'בדוק את האימייל שלך לקבלת קישור האיפוס. אם אינך רואה אותו, בדוק בתיקיית הספאם.',
      'forgot.backToLogin': 'חזרה להתחברות',
      'forgot.error': 'משהו השתבש. אנא נסה שוב.',
      'brand.name': 'BetterChoice',
      'brand.portal': 'פורטל דיאטנים',
      'footer.copyright': '© 2026 BetterChoice. כל הזכויות שמורות.',
    },
  };
  const t = (key) => translations[language]?.[key] || key;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setIsLoading(true);
    try {
      const redirectTo = `${window.location.origin}/reset-password`;
      await authClient.resetPasswordForEmail(email.trim().toLowerCase(), { redirectTo });
      setSuccess(true);
    } catch (err) {
      console.error('Forgot password error:', err);
      setError(err.message || t('forgot.error'));
    } finally {
      setIsLoading(false);
    }
  };

  const inputClasses = `w-full px-4 py-3 ${isDarkMode ? 'bg-moss/50 border-line text-white' : 'bg-canvas border-line text-ink'} border rounded-lg placeholder-ink-muted focus:outline-none focus:ring-2 focus:ring-forest focus:border-transparent transition-all`;
  const labelClasses = `block text-sm font-medium ${themeClasses.textSecondary} mb-2`;

  return (
    <div
      className={`min-h-screen ${themeClasses.bgPrimary} flex items-center justify-center px-4 py-12 transition-colors duration-300`}
      dir={direction}
    >
      <div className="fixed top-4 right-4 z-50 flex gap-2">
        <button
          onClick={toggleTheme}
          className={`${isDarkMode ? 'bg-surface' : 'bg-surface'} text-ink dark:text-ink-dark hover:bg-moss dark:hover:bg-moss-dark p-3 rounded-lg   transition-all duration-300 border ${isDarkMode ? 'border-forest/20' : 'border-forest/30'}`}
          aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
        >
          {isDarkMode ? (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>
        <button
          onClick={toggleLanguage}
          className={`${isDarkMode ? 'bg-surface' : 'bg-surface'} ${isDarkMode ? 'text-forest' : 'text-forest-ondark'} p-3 rounded-lg   transition-all duration-300 border ${isDarkMode ? 'border-forest/20' : 'border-forest/30'} font-semibold`}
          aria-label={language === 'english' ? 'Switch to Hebrew' : 'Switch to English'}
        >
          {language === 'english' ? 'ע' : 'EN'}
        </button>
      </div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 mb-4">
            <img src="/logo.png" alt="BetterChoice Logo" className="w-full h-full object-contain" />
          </div>
          <h1 className={`text-4xl font-bold ${themeClasses.textPrimary} mb-2`}>{t('brand.name')}</h1>
          <p className={`${isDarkMode ? 'text-forest' : 'text-forest-ondark'} text-lg font-medium`}>{t('brand.portal')}</p>
        </div>

        <div className={`${themeClasses.bgCard}  rounded-lg ${themeClasses.shadowCard} p-8 border ${themeClasses.borderPrimary}`}>
          <h2 className={`text-2xl font-semibold ${themeClasses.textPrimary} mb-2 text-center`}>{t('forgot.title')}</h2>
          <p className={`text-sm ${themeClasses.textSecondary} mb-6 text-center`}>{t('forgot.subtitle')}</p>

          {error && (
            <div className={`mb-4 p-3 ${isDarkMode ? 'bg-red-500/20 border-red-500/50 text-red-300' : 'bg-red-100 border-red-300 text-red-700'} border rounded-lg text-sm`}>
              {error}
            </div>
          )}
          {success && (
            <div className={`mb-4 p-3 ${isDarkMode ? 'bg-forest/20 border-forest/50 text-forest' : 'bg-forest-faint border-forest/40 text-forest-ondark'} border rounded-lg text-sm`}>
              {t('forgot.success')}
            </div>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className={labelClasses}>{t('forgot.email')}</label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className={inputClasses}
                  placeholder="dietitian@betterchoice.one"
                />
              </div>
              <button
                type="submit"
                disabled={isLoading}
                className={`w-full ${themeClasses.btnPrimary} text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300   disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {isLoading ? t('forgot.sending') : t('forgot.submit')}
              </button>
            </form>
          )}

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => navigate('/')}
              className={`text-sm font-semibold ${isDarkMode ? 'text-forest-ondark hover:text-forest' : 'text-forest-ondark hover:text-forest-ondark'} transition-colors`}
            >
              {t('forgot.backToLogin')}
            </button>
          </div>
        </div>

        <div className={`mt-8 text-center text-sm ${themeClasses.textMuted}`}>
          <p>{t('footer.copyright')}</p>
        </div>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
